import { useAllPullRequestCounts } from '@internal/backstage-plugin-pull-requests';
import {
  MyIssuesTable,
  useFetchIssues,
  JiraIssueSummary,
} from '@internal/plugin-jira-issues';
import '@vscode/codicons/dist/codicon.css';
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAsync } from 'react-use';

import {
  identityApiRef,
  useApi,
  useAnalytics,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { HomePageStarredEntities } from '@backstage/plugin-home';

import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Button,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import RefreshIcon from '@material-ui/icons/Refresh';

import { AnalyticsWrapper } from '../catalog/Components/AnalyticsWrapper';
import { JiraIssuesLogo } from '../home/JiraIssuesLogo';
import { OverviewLogo } from '../home/OverviewLogo';
import { HomeToolKit } from './HomeToolKit';
import { MyTeamsLogo } from './MyTeamsLogo';
import { QuickCatalogAccess } from './QuickCatalogAccess';
import { UserGroupsTable } from './UserGroupsTable';
import WorkSummaryCard from './WorkSummaryCard';
import { ActionRequired, useProposedOwnershipRepos } from './action-required';

const ANALYTICS_PLUGIN_ID = 'pull-request-inbox';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    minHeight: '100vh',
    fontFamily: '"Proxima Nova", ui-sans-serif, system-ui, sans-serif',
    fontSize: 14,
    overflow: 'hidden',
    width: '100%',
  },
  navIcon: {
    marginRight: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
  },
  sidebar: {
    width: 240,
    flexShrink: 0,
    paddingTop: 24,
    marginTop: 24,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '16px',
    backgroundColor: theme.palette.background.default,
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    borderRadius: 6,
    margin: '2px 12px',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.active': {
      backgroundColor: theme.palette.action.selected,
      color: theme.palette.text.primary,
      fontWeight: 600,
    },
  },
  myWorkSection: {
    paddingTop: 8,
    textTransform: 'none',
  },
  myWorkHeader: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(3),
    marginBottom: theme.spacing(1),
    textTransform: 'none',
  },
  badge: {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    fontSize: 11,
    fontWeight: 500,
    padding: '2px 6px',
    borderRadius: 10,
    minWidth: 16,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 'auto',
  },
  badgeRed: {
    backgroundColor:
      theme.palette.type === 'dark' ? 'rgba(244, 67, 54, 0.2)' : '#ffebee',
    color: theme.palette.type === 'dark' ? '#ff5252' : '#c62828',
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: 10,
    minWidth: 16,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 'auto',
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
    minWidth: 0,
  },
  header: {
    marginBottom: theme.spacing(3),
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    padding: theme.spacing(3),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[1],
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  helpLink: {
    color: theme.palette.text.secondary,
    textDecoration: 'none',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  welcomeText: {
    color: theme.palette.text.secondary,
    marginBottom: 4,
    fontSize: 14,
    fontWeight: 400,
  },
  welcomeTitle: {
    fontWeight: 400,
    color: theme.palette.text.primary,
  },
  card: {
    marginBottom: theme.spacing(3),
    boxShadow: theme.shadows[1],
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  sectionTitle: {
    fontWeight: 400,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    fontSize: 14,
  },
  divider: {
    margin: '8px 0',
    backgroundColor: theme.palette.divider,
    marginLeft: 16,
    marginRight: 16,
  },
  cardSpacing: {
    marginBottom: theme.spacing(0.1),
  },
  responsiveTableWrapper: {
    width: '100%',
    '& table': {
      width: '100%',
      tableLayout: 'fixed',
      minWidth: 'auto !important',
    },
    '& th, & td': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      padding: theme.spacing(1),
      [theme.breakpoints.down('sm')]: {
        whiteSpace: 'normal',
      },
    },
  },
  responsiveWorkSummary: {
    width: '100%',
    '& table': {
      width: '100%',
      tableLayout: 'fixed',
    },
    '& th, & td': {
      overflowWrap: 'anywhere',
    },
  },
}));

const PullRequestsNeedsReview = lazy(() =>
  import('@internal/backstage-plugin-pull-requests').then(m => ({
    default: m.PullRequestsNeedsReview,
  })),
);
const PullRequestsReturned = lazy(() =>
  import('@internal/backstage-plugin-pull-requests').then(m => ({
    default: m.PullRequestsReturned,
  })),
);
const PullRequestsApproved = lazy(() =>
  import('@internal/backstage-plugin-pull-requests').then(m => ({
    default: m.PullRequestsApproved,
  })),
);
const PullRequestsWaitingAuthor = lazy(() =>
  import('@internal/backstage-plugin-pull-requests').then(m => ({
    default: m.PullRequestsWaitingAuthor,
  })),
);
const PullRequestsOpened = lazy(() =>
  import('@internal/backstage-plugin-pull-requests').then(m => ({
    default: m.PullRequestsOpened,
  })),
);
const PullRequestsMerged = lazy(() =>
  import('@internal/backstage-plugin-pull-requests').then(m => ({
    default: m.PullRequestsMerged,
  })),
);

type PullRequestTypeLocal =
  | 'approved'
  | 'merged'
  | 'needsReview'
  | 'opened'
  | 'returned'
  | 'waitingAuthor';

interface SectionWithRefreshProps {
  title: string;
  type: PullRequestTypeLocal;
  children: React.ReactNode;
  onGlobalRefresh?: () => void;
}

const SectionWithRefresh = ({
  title,
  type,
  children,
  onGlobalRefresh,
}: SectionWithRefreshProps) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const analytics = useAnalytics();

  const handleRefresh = () => {
    analytics.captureEvent('click', 'refresh', {
      attributes: {
        pluginId: ANALYTICS_PLUGIN_ID,
        section: type,
        title,
      },
    });

    setRefreshKey(k => k + 1);
    onGlobalRefresh?.();
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">{title}</Typography>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={handleRefresh}
          startIcon={<RefreshIcon />}
        >
          Refresh
        </Button>
      </Box>
      {React.cloneElement(children as React.ReactElement, { refreshKey })}
    </Box>
  );
};

export const HomePage = () => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedSection, setSelectedSection] = useState('overview');
  const [refreshKey, setRefreshKey] = useState(0);
  const analytics = useAnalytics();

  const handleGlobalRefresh = () => {
    setRefreshKey(k => k + 1);
  };

  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  const { issues, loading: jiraLoading, error: jiraError } = useFetchIssues();

  const [issueCounts, setIssueCounts] = useState({
    total: 0,
    toDo: 0,
    inProgress: 0,
    inReview: 0,
  });

  useEffect(() => {
    if (issues) {
      const toDoCount = issues.filter(
        (i: JiraIssueSummary) => i.status === 'To Do',
      ).length;
      const inProgressCount = issues.filter(
        (i: JiraIssueSummary) => i.status === 'In Progress',
      ).length;
      const inReviewCount = issues.filter(
        (i: JiraIssueSummary) => i.status === 'In Review',
      ).length;
      const total = issues.length;

      setIssueCounts(prevCounts => {
        if (
          prevCounts.total === total &&
          prevCounts.toDo === toDoCount &&
          prevCounts.inProgress === inProgressCount &&
          prevCounts.inReview === inReviewCount
        ) {
          return prevCounts;
        }
        return {
          total,
          toDo: toDoCount,
          inProgress: inProgressCount,
          inReview: inReviewCount,
        };
      });
    }
  }, [issues]);

  const {
    counts: pullRequestCounts,
    loading: prLoading,
    error: prError,
  } = useAllPullRequestCounts(refreshKey);

  const { repos: actionRequiredRepos } = useProposedOwnershipRepos();

  const { value: userName } = useAsync(async (): Promise<string> => {
    const userEntityRef = (await identityApi.getBackstageIdentity())
      .userEntityRef;

    const entity = await catalogApi.getEntityByRef(userEntityRef);
    return entity?.metadata?.title ?? 'User';
  }, [identityApi, catalogApi]);

  const counts = {
    openPRs: pullRequestCounts?.opened ?? 0,
    needReview: pullRequestCounts?.needsReview ?? 0,
    approved: pullRequestCounts?.approved ?? 0,
    returned: pullRequestCounts?.returned ?? 0,
    waitingForAuthor: pullRequestCounts?.waitingAuthor ?? 0,
    issues: issueCounts.total,
    merged: pullRequestCounts?.merged ?? 0,
  };

  useEffect(() => {
    const validSections = new Set([
      'overview',
      'my-teams',
      'needs-review',
      'returned',
      'approved',
      'waiting-for-author',
      'open-prs',
      'merged',
      'issues',
      'action-required',
    ]);
    const params = new URLSearchParams(location.search);
    const selectedSectionParam = params.get('tab') || 'overview';
    if (validSections.has(selectedSectionParam)) {
      setSelectedSection(selectedSectionParam);
    }
  }, [location.search]);

  const handleSectionChange = (sectionId: string) => {
    const prInboxSections = [
      'needs-review',
      'returned',
      'approved',
      'waiting-for-author',
      'open-prs',
      'merged',
    ];

    if (prInboxSections.includes(sectionId)) {
      analytics.captureEvent('navigate', sectionId, {
        attributes: {
          pluginId: ANALYTICS_PLUGIN_ID,
          section: sectionId,
          from: selectedSection,
        },
      });
    }

    setSelectedSection(sectionId);
    navigate({
      pathname: location.pathname,
      search: sectionId === 'overview' ? '' : `?tab=${sectionId}`,
    });
  };

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: <OverviewLogo /> },
    { id: 'my-teams', label: 'My Teams', icon: <MyTeamsLogo /> },
  ];

  const myWorkItems = [
    {
      id: 'needs-review',
      label: 'Needs your review',
      icon: <span className="codicon codicon-git-pull-request" />,
      count: counts.needReview || 0,
    },
    {
      id: 'returned',
      label: 'Returned to you',
      icon: <span className="codicon codicon-git-pull-request-go-to-changes" />,
      count: counts.returned || 0,
    },
    {
      id: 'waiting-for-author',
      label: 'Waiting for Author',
      icon: <span className="codicon codicon-git-pull-request-new-changes" />,
      count: counts.waitingForAuthor || 0,
    },
    {
      id: 'approved',
      label: 'Approved',
      icon: <span className="codicon codicon-git-pull-request-done" />,
      count: counts.approved || 0,
    },
    {
      id: 'open-prs',
      label: 'Waiting for Reviewers',
      icon: <span className="codicon codicon-git-pull-request-create" />,
      count: counts.openPRs || 0,
    },
    {
      id: 'merged',
      label: 'Recently Merged',
      icon: <span className="codicon codicon-git-merge" />,
      count: counts.merged || 0,
    },
  ];

  const jiraItems = [
    {
      id: 'issues',
      label: 'Issues',
      icon: <JiraIssuesLogo />,
      count: counts.issues || 0,
    },
  ];

  const actionItems = [
    {
      id: 'action-required',
      label: 'Action Required',
      icon: <span className="codicon codicon-bell-dot" />,
      count: actionRequiredRepos.length,
    },
  ];

  const renderContent = () => {
    if (jiraLoading || prLoading) {
      return (
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="200px"
          >
            <CircularProgress />
            <Typography style={{ marginLeft: 10 }}>Loading...</Typography>
          </Box>
        </Grid>
      );
    }

    if (jiraError || prError) {
      return (
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="200px"
          >
            <Typography color="error">
              Error loading data: {jiraError?.message || prError?.message}
            </Typography>
          </Box>
        </Grid>
      );
    }

    return (
      <Grid container spacing={3} style={{ margin: 0, width: '100%' }}>
        {selectedSection === 'overview' && (
          <Grid item xs={12}>
            <QuickCatalogAccess
              onMyTeamsClick={() => handleSectionChange('my-teams')}
            />
            <Box mt={1} className={classes.responsiveWorkSummary}>
              <WorkSummaryCard
                onItemClick={handleSectionChange}
                issueCount={issueCounts.total}
                needsReviewsCount={counts.needReview}
                returnedCount={counts.returned}
                approvedCount={counts.approved}
                waitingForAuthorCount={counts.waitingForAuthor}
                openPRsCount={counts.openPRs}
                mergedCount={counts.merged}
              />
            </Box>
            <Box mt={1}>
              <HomeToolKit />
            </Box>
            <Box mt={2}>
              <HomePageStarredEntities />
            </Box>
          </Grid>
        )}

        {selectedSection === 'needs-review' && (
          <Grid item xs={12}>
            <Suspense fallback={<CircularProgress />}>
              <AnalyticsWrapper pluginId={ANALYTICS_PLUGIN_ID}>
                <SectionWithRefresh
                  title="Needs your review"
                  type="needsReview"
                  onGlobalRefresh={handleGlobalRefresh}
                >
                  <PullRequestsNeedsReview />
                </SectionWithRefresh>
              </AnalyticsWrapper>
            </Suspense>
          </Grid>
        )}

        {selectedSection === 'returned' && (
          <Grid item xs={12}>
            <Suspense fallback={<CircularProgress />}>
              <AnalyticsWrapper pluginId={ANALYTICS_PLUGIN_ID}>
                <SectionWithRefresh
                  title="Returned to you"
                  type="returned"
                  onGlobalRefresh={handleGlobalRefresh}
                >
                  <PullRequestsReturned />
                </SectionWithRefresh>
              </AnalyticsWrapper>
            </Suspense>
          </Grid>
        )}

        {selectedSection === 'approved' && (
          <Grid item xs={12}>
            <Suspense fallback={<CircularProgress />}>
              <AnalyticsWrapper pluginId={ANALYTICS_PLUGIN_ID}>
                <SectionWithRefresh
                  title="Approved"
                  type="approved"
                  onGlobalRefresh={handleGlobalRefresh}
                >
                  <PullRequestsApproved />
                </SectionWithRefresh>
              </AnalyticsWrapper>
            </Suspense>
          </Grid>
        )}

        {selectedSection === 'waiting-for-author' && (
          <Grid item xs={12}>
            <Suspense fallback={<CircularProgress />}>
              <AnalyticsWrapper pluginId={ANALYTICS_PLUGIN_ID}>
                <SectionWithRefresh
                  title="Waiting for Author"
                  type="waitingAuthor"
                  onGlobalRefresh={handleGlobalRefresh}
                >
                  <PullRequestsWaitingAuthor />
                </SectionWithRefresh>
              </AnalyticsWrapper>
            </Suspense>
          </Grid>
        )}

        {selectedSection === 'open-prs' && (
          <Grid item xs={12}>
            <Suspense fallback={<CircularProgress />}>
              <AnalyticsWrapper pluginId={ANALYTICS_PLUGIN_ID}>
                <SectionWithRefresh
                  title="Waiting for Reviewers"
                  type="opened"
                  onGlobalRefresh={handleGlobalRefresh}
                >
                  <PullRequestsOpened />
                </SectionWithRefresh>
              </AnalyticsWrapper>
            </Suspense>
          </Grid>
        )}

        {selectedSection === 'merged' && (
          <Grid item xs={12}>
            <Suspense fallback={<CircularProgress />}>
              <AnalyticsWrapper pluginId={ANALYTICS_PLUGIN_ID}>
                <SectionWithRefresh
                  title="Recently Merged"
                  type="merged"
                  onGlobalRefresh={handleGlobalRefresh}
                >
                  <PullRequestsMerged />
                </SectionWithRefresh>
              </AnalyticsWrapper>
            </Suspense>
          </Grid>
        )}

        {selectedSection === 'issues' && (
          <Grid item xs={12}>
            <MyIssuesTable issues={issues || []} issueCounts={issueCounts} />
          </Grid>
        )}

        {selectedSection === 'my-teams' && (
          <Grid item xs={12}>
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Groups you are a member of
                </Typography>
                <UserGroupsTable />
              </CardContent>
            </Card>
          </Grid>
        )}

        {selectedSection === 'action-required' && (
          <Grid item xs={12}>
            <ActionRequired />
          </Grid>
        )}

        {![
          'overview',
          'needs-review',
          'returned',
          'approved',
          'waiting-for-author',
          'open-prs',
          'merged',
          'issues',
          'my-teams',
          'action-required',
        ].includes(selectedSection) && (
          <Grid item xs={12}>
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6" className={classes.sectionTitle}>
                  {selectedSection.replace('-', ' ').toUpperCase()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Content for {selectedSection} section
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <Grid
      container
      className={classes.root}
      style={{ margin: 0, width: '100%' }}
    >
      <Grid item sm={2} className={classes.sidebar}>
        {sidebarItems.map(item => {
          const navItem = (
            <div
              key={item.id}
              role="button"
              tabIndex={0}
              onClick={() => handleSectionChange(item.id)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleSectionChange(item.id);
                }
              }}
              className={`${classes.navItem} ${
                selectedSection === item.id ? 'active' : ''
              }`}
            >
              <div className={classes.navIcon}>{item.icon}</div>
              {item.label}
            </div>
          );

          return (
            <React.Fragment key={item.id}>
              {navItem}
              {item.id === 'overview' && (
                <Divider className={classes.divider} />
              )}
            </React.Fragment>
          );
        })}

        <Divider className={classes.divider} />

        <div className={classes.myWorkSection}>
          <Typography variant="overline" className={classes.myWorkHeader}>
            Pull Request Inbox
          </Typography>

          {myWorkItems.map(item => (
            <div
              key={item.id}
              role="button"
              tabIndex={0}
              className={`${classes.navItem} ${
                selectedSection === item.id ? 'active' : ''
              }`}
              onClick={() => handleSectionChange(item.id)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleSectionChange(item.id);
                }
              }}
            >
              <div className={classes.navIcon}>{item.icon}</div>
              {item.label}
              {item.count > 0 && (
                <div className={classes.badge}>{item.count}</div>
              )}
            </div>
          ))}
        </div>

        <Divider className={classes.divider} />

        <div className={classes.myWorkSection}>
          <Typography variant="overline" className={classes.myWorkHeader}>
            My Jira Issues
          </Typography>

          {jiraItems.map(item => (
            <div
              key={item.id}
              role="button"
              tabIndex={0}
              className={`${classes.navItem} ${
                selectedSection === item.id ? 'active' : ''
              }`}
              onClick={() => handleSectionChange(item.id)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleSectionChange(item.id);
                }
              }}
            >
              <div className={classes.navIcon}>{item.icon}</div>
              {item.label}
              {item.count > 0 && (
                <div className={classes.badge}>{item.count}</div>
              )}
            </div>
          ))}
        </div>

        <Divider className={classes.divider} />

        <div className={classes.myWorkSection}>
          <Typography variant="overline" className={classes.myWorkHeader}>
            Action Required
          </Typography>

          {actionItems.map(item => (
            <div
              key={item.id}
              role="button"
              tabIndex={0}
              className={`${classes.navItem} ${
                selectedSection === item.id ? 'active' : ''
              }`}
              onClick={() => handleSectionChange(item.id)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleSectionChange(item.id);
                }
              }}
            >
              <div className={classes.navIcon}>{item.icon}</div>
              {item.label}
              {item.count > 0 && (
                <div className={classes.badgeRed}>{item.count}</div>
              )}
            </div>
          ))}
        </div>

        <Divider className={classes.divider} />
      </Grid>
      <Grid item sm={10} className={classes.content}>
        <Grid item sm={12} className={classes.header}>
          <div>
            <Typography className={classes.welcomeText}>
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </Typography>
            <Typography variant="h5" className={classes.welcomeTitle}>
              Welcome, {userName ?? 'User'}
            </Typography>
          </div>
          <a
            href="https://frontstage.tomtomgroup.com/docs/default/component/frontstage/"
            target="_blank"
            rel="noopener noreferrer"
            className={classes.helpLink}
          >
            <HelpOutlineIcon style={{ fontSize: 20 }} />
          </a>
        </Grid>

        {renderContent()}
      </Grid>
    </Grid>
  );
};
export default HomePage;
